import { Question, StringObj } from "../../type";
import { headerBlank, pageBreaks } from "./move";
import { checkExcelFormat } from "../review/excel";
import { getQuestionRowsCount, transformQuestionsArrayToJSON } from "./transform";
import { judgeQuestionId, removeBr, removeSpace } from "./utils";

const questionItemCols: string[] = [
  "questionId",
  "questionTitle",
  "questionText",
  "questionType",
  "questionCount",
  "condition",
  "remarks",
];

/**
 * 質問項目シートから質問項目をloadする関数
 */
export async function getQuestionTitlesFromItemList(sheetName: string) {
  const questionItems = await Excel.run(async (context) => {
    const sheet = context.workbook.worksheets.getItem(sheetName);
    const range = sheet.getRange("D8:J100");
    range.load("values");
    await context.sync();

    const values = range.values;

    // 配列をオブジェクトの配列形式に変換
    const questionItems = values
      .filter((row) => row.some((cell) => cell !== "")) // 空行を除外
      .map((row) => {
        const rowObj: StringObj = {};
        row.forEach((cell, i) => {
          // rowObj[questionItemCols[i]] = cell;

          let cleanedValue = cell.toString();
          cleanedValue = cleanedValue.replace("　", " ");
          if (cleanedValue) {
            if ([0].includes(i) && !cleanedValue.match(/^(Q|QS|SQ|F)\d+$/)) {
              cleanedValue = `Q${cleanedValue}`;
            }
            if ([0, 3].includes(i)) {
              cleanedValue = removeSpace(cleanedValue);
            }
            if ([0, 1, 3, 4, 5].includes(i)) {
              cleanedValue = removeBr(cleanedValue);
            }
            if (i == 5 && cleanedValue.includes("＝")) {
              cleanedValue = cleanedValue.replace("＝", "=");
            }
            if (i == 3) {
              cleanedValue = cleanedValue
                .replace("ﾏﾄﾘｸｽ", "マトリクス")
                .replace("(", "（")
                .replace(")", "）");
            }
          }
          rowObj[questionItemCols[i]] = cleanedValue;
        });
        return rowObj;
      });

    return questionItems;
  });

  return questionItems;
}

export async function loadSheetValues(sheetName: string, rangeText?: string): Promise<any> {
  const results = await Excel.run(async (context) => {
    try {
      const sheet = context.workbook.worksheets.getItem(sheetName);
      const range = rangeText ? sheet.getRange(rangeText) : sheet.getUsedRange();
      await context.sync();
      range.load("values");

      await context.sync();
      const values = range.values;

      let checkResultsExcel = [];

      const checkResults1 = await checkExcelFormat(values);
      checkResultsExcel = checkResultsExcel.concat(checkResults1);
      const { questions, checkResults2 } = transformQuestionsArrayToJSON(values);
      checkResultsExcel = checkResultsExcel.concat(checkResults2);

      return { questions, checkResultsExcel };
    } catch (error) {
      console.error("Error loading sheet values:", error);
      return { questions: [], checkResultsExcel: [] };
    }
  });

  return results;
}

export async function getLastActiveRow(sheetName?: string) {
  const results = await Excel.run(async (context) => {
    // シートの値を読み込む
    let sheet = undefined;

    if (sheetName) {
      sheet = context.workbook.worksheets.getItem(sheetName);
    } else {
      sheet = context.workbook.worksheets.getActiveWorksheet();
    }

    const usedRange = sheet.getRange(`C${headerBlank}:N9999`);
    usedRange.load("values, rowCount");
    await context.sync();

    let lastActiveRow = 0;
    for (let i = 0; i < usedRange.rowCount; i++) {
      if (usedRange.values[i].filter((v) => v).length) {
        lastActiveRow = i;
      }
    }
    return lastActiveRow == 0 ? lastActiveRow + headerBlank : lastActiveRow + headerBlank + 1;
  });
  return results;
}

export async function getQuestionRange(
  question: Question,
  nextQuestionId?: string,
  sheetName?: string
) {
  const { excel } = await getQuestionIds([], sheetName || "");

  if (!excel.includes(question.questionId)) {
    // throw new Error(`Question ID ${question.questionId} not found in the sheet.`);
    return {
      rangeText: "B5",
      firstRowIndex: 0,
      firstRowIndexNext: 0,
      isLastQuestion: false,
      questionRowsCount: 0,
      pageBreakCount: 0,
      supplimentCount: 0,
      lastActiveRow: 0,
    };
  }

  const results = await Excel.run(async (context) => {
    try {
      // シートの値を読み込む
      let sheet = undefined;

      if (sheetName) {
        sheet = context.workbook.worksheets.getItem(sheetName);
      } else {
        sheet = context.workbook.worksheets.getActiveWorksheet();
      }

      const usedRange = sheet.getRange(`C${headerBlank + 1}:N9999`);
      usedRange.load("values, rowCount");
      await context.sync();

      let firstRowIndex = 0;
      let firstRowUpdate = false;
      let firstRowIndexNext = 0;
      let isLastQuestion = false;
      let lastIndex = 0;
      let pageBreakCount = 0;
      let supplimentCount = 0;

      for (let i = 0; i < usedRange.rowCount; i++) {
        const cValue = usedRange.values[i][0].toString();
        const dValue = usedRange.values[i][1].toString();
        const dValuePrev = i >= 1 ? usedRange.values[i - 1][1].toString() : "";

        if (cValue === question.questionId || `Q${cValue}` === question.questionId) {
          firstRowIndex = i;
          firstRowUpdate = true;
        }
        if (nextQuestionId) {
          if (
            cValue === nextQuestionId ||
            `Q${cValue}` === nextQuestionId
            //  ||
            // (dValue == "ページの始まり" && firstRowUpdate)
          ) {
            firstRowIndexNext = i;
          }

          if (firstRowUpdate && firstRowIndexNext <= 1 && pageBreaks.includes(dValue)) {
            pageBreakCount++;
          }
          if (
            firstRowUpdate &&
            firstRowIndexNext <= 1 &&
            dValue == "設問補足文" &&
            pageBreaks.includes(dValuePrev)
          ) {
            supplimentCount++;
          }
        }
        // else {
        //   if (cValue) {
        //     firstRowIndexNext = i;
        //   }
        // }

        if (usedRange.values[i].filter((v) => v).length) {
          lastIndex = i;
        }

        if (firstRowIndex > 0 && firstRowIndexNext > 1) {
          break;
        }
      }

      let lastActiveRow = 0;
      for (let i = 0; i < usedRange.rowCount; i++) {
        if (usedRange.values[i].filter((v) => v).length) {
          lastActiveRow = i;
        }
      }

      const questionRowsCount = getQuestionRowsCount(question);

      if (firstRowIndex === 0 && !firstRowUpdate) {
        firstRowIndex = lastActiveRow == 0 ? 0 : lastActiveRow + 1;
      }

      if (firstRowIndexNext <= 1 || !nextQuestionId) {
        isLastQuestion = true;
        // firstRowIndexNext = firstRowIndex + questionRowsCount + 1;
        firstRowIndexNext = lastIndex + 1;
      }

      if (question.questionSupplementPrev.length) {
        firstRowIndex -= question.questionSupplementPrev.length;
      }

      return {
        rangeText: `C${firstRowIndex + headerBlank + 1}:N${firstRowIndexNext + headerBlank + 1 - 1 - supplimentCount - pageBreakCount}`,
        firstRowIndex,
        firstRowIndexNext,
        isLastQuestion,
        questionRowsCount,
        pageBreakCount,
        supplimentCount,
        lastActiveRow: lastActiveRow + headerBlank + 1,
      };
    } catch (error) {
      console.error("Error getting question range:", error);
      return {
        rangeText: "B5",
        firstRowIndex: 0,
        firstRowIndexNext: 0,
        isLastQuestion: false,
        questionRowsCount: 0,
        pageBreakCount: 0,
        supplimentCount: 0,
        lastActiveRow: 0,
      };
    }
  });

  return results;
}

export async function getQuestionRangeWithId(
  questionId: string,
  nextQuestionId?: string,
  sheetName?: string
) {
  const results = await Excel.run(async (context) => {
    // シートの値を読み込む
    let sheet = undefined;

    if (sheetName) {
      sheet = context.workbook.worksheets.getItem(sheetName);
    } else {
      sheet = context.workbook.worksheets.getActiveWorksheet();
    }

    const usedRange = sheet.getRange(`C${headerBlank + 1}:N9999`);
    usedRange.load("values, rowCount");
    await context.sync();

    let firstRowIndex = 0;
    let firstRowUpdate = false;
    let firstRowIndexNext = 0;
    // let isLastQuestion = false;
    let lastIndex = 0;
    let pageBreakCount = 0;
    let supplimentCount = 0;

    let continurous = true;
    let lastIndexInQuestion = 0;

    for (let i = 0; i < usedRange.rowCount; i++) {
      const cValue = usedRange.values[i][0].toString();
      const dValue = usedRange.values[i][1].toString();
      const dValuePrev = i >= 1 ? usedRange.values[i - 1][1].toString() : "";

      const fValue = usedRange.values[i][3].toString();
      const gValue = usedRange.values[i][4].toString();

      if (!fValue && !gValue && firstRowUpdate) {
        continurous = false;
      }
      if (!nextQuestionId) {
        if (!continurous) {
          lastIndexInQuestion = i;
          break;
        }
      } else if (!continurous && firstRowIndexNext <= 1) {
        lastIndexInQuestion = i;
      }

      if (cValue === questionId || `Q${cValue}` === questionId) {
        firstRowIndex = i;
        firstRowUpdate = true;
      }
      if (nextQuestionId) {
        if (cValue === nextQuestionId || `Q${cValue}` === nextQuestionId) {
          firstRowIndexNext = i;
        }

        if (firstRowUpdate && firstRowIndexNext <= 1 && pageBreaks.includes(dValue)) {
          pageBreakCount++;
        }
        if (
          firstRowUpdate &&
          firstRowIndexNext <= 1 &&
          dValue == "設問補足文" &&
          pageBreaks.includes(dValuePrev)
        ) {
          supplimentCount++;
        }
      }

      if (usedRange.values[i].filter((v) => v).length) {
        lastIndex = i;
      }

      if (firstRowIndex > 0 && firstRowIndexNext > 1) {
        break;
      }
    }
    // 該当のQ番号から上に進んで、ページの始まりより前に設問補足文が来るか、値が続いている中でページの始まりがくるかを確認する
    const upperRange = sheet.getRange(`C${headerBlank + 1}:D${headerBlank + firstRowIndex}`);
    upperRange.load("values");
    await context.sync();

    // ページ改があるかを確認
    let passBreak = false;
    let includePageBreak = false;
    let supplementCount = 0;
    let continurous2 = true;
    for (const r of upperRange.values.reverse().slice(0, 4)) {
      if (!r[1]) {
        continurous2 = false;
      }

      if (passBreak || !continurous2) {
        break;
      }
      if (r[1] === "ページの始まり") {
        passBreak = true;
        includePageBreak = true;
      } else if (r[1] === "設問補足文") {
        supplementCount++;
      }
    }

    const rangeFrom =
      firstRowIndex + headerBlank + 1 + (includePageBreak ? -1 : 0) - supplementCount;
    const rangeTo = nextQuestionId
      ? firstRowIndexNext + headerBlank + 1 - 1 - supplimentCount - pageBreakCount
      : lastIndexInQuestion + headerBlank + 1;

    return {
      rangeText: `C${rangeFrom}:N${rangeTo}`,
      rangeFrom,
      supplementCount,
      includePageBreak,
    };
  });

  return results;
}

export async function getQuestionIds(questions: Question[], editSheet: string) {
  const questionIdsAddin = questions.map((q) => q.questionId);

  const questionIdsExcel = await Excel.run(async (context) => {
    const sheet = context.workbook.worksheets.getItem(editSheet);
    const range = sheet.getRange(`C${headerBlank + 1}:C9999`);
    range.load("values");
    await context.sync();

    // C列の文字列を全て取得する
    const columnCValues = range.values.map((row) => row[0]).filter((value) => value);
    const questionIds = columnCValues.map((value) => {
      if (typeof value == "string" && judgeQuestionId(value)) {
        return value;
      } else {
        return `Q${value}`;
      }
    });

    return questionIds;
  });

  return { addin: questionIdsAddin, excel: questionIdsExcel };
}

export async function judgeEmptyFormSheet(sheetName: string) {
  const results = await Excel.run(async (context) => {
    const sheet = context.workbook.worksheets.getItem(sheetName);

    // B5:N5が空かどうかを確認
    const range = sheet.getRange("B5:N5");
    range.load("values");
    await context.sync();
    const isEmpty = range.values[0].every((cell) => cell === null || cell === "");
    return isEmpty;
  });

  return results;
}
